import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { formatDateOnly } from '../../../../utils/dateUtils';
import SapDealerByCountryDropdown from '../../../../components/common/SapDealerByCountryDropdown';
import GenericTableFilter from '../../../../components/common/GenericTableFilter';
import useAuditList from './hooks/useAuditList';
import useWarrantyAuditPdf from './hooks/useWarrantyAuditPdf';

const WarrantyAuditFormListPage = () => {
    const { th } = useLanguage();
    const navigate = useNavigate();
    const { results, isLoading, applyFilters, resetFilters } = useAuditList();
    const { generate, isGenerating } = useWarrantyAuditPdf();

    const filterFields = useMemo(
        () => [
            {
                field: 'bosBranchCode',
                headerName: th('warrantyAudit.fields.dealer') || 'Bayi/Servis',
                type: 'custom',
                customComponent: ({ value, onChange }) => (
                    <SapDealerByCountryDropdown value={value} onChange={onChange} className="w-full" />
                )
            },
            {
                field: 'fromDate',
                headerName: th('warrantyAudit.fields.fromDate') || 'Başlangıç Tarihi',
                type: 'date'
            },
            {
                field: 'toDate',
                headerName: th('warrantyAudit.fields.toDate') || 'Bitiş Tarihi',
                type: 'date'
            }
        ],
        [th]
    );

    const initialFilters = useMemo(() => ({ bosBranchCode: null, fromDate: null, toDate: null }), []);

    const bandBody = (row) => (
        <Tag
            value={row.scoreBandText}
            style={{ backgroundColor: row.scoreBandColor || '#64748b', color: '#fff' }}
        />
    );

    const actionsBody = (row) => (
        <div className="flex align-items-center justify-content-center gap-3">
            <i
                onClick={() => navigate(`/warranty-management/warranty-audit/form/${row.id}`)}
                className="pi pi-eye cursor-pointer text-blue-500 hover:text-blue-700"
                style={{ fontSize: '1.1rem' }}
                title={th('warrantyAudit.actions.view') || 'Detay'}
            />
            <i
                onClick={() => {
                    if (!isGenerating) generate(row.id, { download: false });
                }}
                className={`pi ${isGenerating ? 'pi-spin pi-spinner' : 'pi-file-pdf'} cursor-pointer ${isGenerating ? 'text-blue-500' : 'text-red-500 hover:text-red-700'}`}
                style={{ fontSize: '1.1rem' }}
                title={th('warrantyAudit.actions.pdf') || 'PDF'}
            />
        </div>
    );

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                <h1 className="m-0">{th('warrantyAudit.list.pageTitle') || 'Denetim Giriş ve Sonuçları'}</h1>
                <Button
                    label={th('warrantyAudit.list.newAudit') || 'Yeni Denetim'}
                    icon="pi pi-plus"
                    onClick={() => navigate('/warranty-management/warranty-audit/form/create')}
                />
            </div>

            <div className="mb-4">
                <GenericTableFilter
                    filterFields={filterFields}
                    initialFilters={initialFilters}
                    onSearch={() => {}}
                    onApplyFilters={applyFilters}
                    onResetFilters={resetFilters}
                />
            </div>

            <DataTable
                value={results}
                loading={isLoading}
                paginator
                rows={10}
                emptyMessage={th('warrantyAudit.list.empty') || 'Kayıt bulunamadı'}
                showGridlines
                stripedRows
                tableStyle={{ minWidth: '50rem' }}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate={th('table.paginatorTemplate')}
            >
                <Column body={actionsBody} exportable={false} bodyClassName="text-center" headerClassName="text-center" style={{ width: '5%', minWidth: '70px' }} />
                <Column field="id" header={th('warrantyAudit.fields.auditNo') || 'Denetim Nr'} sortable style={{ width: '110px' }} />
                <Column
                    field="auditDate"
                    header={th('warrantyAudit.fields.auditDate') || 'Denetim Tarihi'}
                    body={(row) => formatDateOnly(row.auditDate)}
                    sortable
                />
                <Column field="dealerName" header={th('warrantyAudit.fields.dealer') || 'Bayi/Servis'} sortable />
                <Column field="auditorName" header={th('warrantyAudit.fields.auditor') || 'Denetçi'} sortable />
                <Column field="totalScore" header={th('warrantyAudit.fields.totalScore') || 'Toplam Puan'} sortable style={{ width: '130px' }} />
                <Column header={th('warrantyAudit.fields.result') || 'Sonuç'} body={bandBody} style={{ width: '160px' }} />
            </DataTable>
        </div>
    );
};

export default WarrantyAuditFormListPage;
